import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { PermissionFlags } from '../enums/permissionFlags.enum';
import { SecurityProfileEnum } from '../enums/securityProfile.enum';
import { LoginResponse } from '../models/loginResponse.model';
import { StoredViewAsLabels, ViewAsBannerState, ViewAsLabels, ViewAsSelection } from '../models/viewAs.model';
import { LocalStorageConstants } from '../shared/constants/localStorage.constants';
import { RolePermissionObjects } from '../shared/constants/rolePermissionObjects.constants';
import { RoutePaths } from '../shared/constants/routePaths.constants';
import { VIEW_AS_ENDED_MESSAGE } from '../shared/constants/viewAs.constants';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class ViewAsService implements OnDestroy {
    private readonly loadedStateKey: string;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly authService: AuthService,
        private readonly permissionService: PermissionService,
        private readonly router: Router,
        private readonly toastService: ToastService,
        private readonly translateService: TranslateService,
        private readonly ngZone: NgZone,
    ) {
        this.loadedStateKey = toStateKey(this.authService.getViewAsState());
        if (!this.authService.isViewAsActive()) {
            localStorage.removeItem(LocalStorageConstants.viewAsLabels);
        }
        this.authService.viewAsEnded$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => this.onPreviewDropped(),
        });
        window.addEventListener('storage', this.onStorageChange);
        this.showEndedNotice();
    }

    ngOnDestroy(): void {
        window.removeEventListener('storage', this.onStorageChange);
        this.destroy$.next();
        this.destroy$.complete();
    }

    get isActive(): boolean {
        return this.authService.isViewAsActive();
    }

    get isReadonlyContext(): boolean {
        return this.authService.isReadonlyContext();
    }

    // During a preview the permission map is the previewed role's (with writes stripped), so it cannot
    // answer for the admin's own role; the admin was eligible when the preview started and the backend
    // re-checks the real role on every Start, so switching stays offered.
    canStartViewAs(): boolean {
        if (this.authService.getCurrentUser()?.userType !== SecurityProfileEnum.Admin) {
            return false;
        }
        return this.isActive || this.permissionService.hasPermissionFlag(
            RolePermissionObjects.RolePermission,
            PermissionFlags.UpdateMy | PermissionFlags.UpdateHierarchy,
        );
    }

    getBannerState(): ViewAsBannerState | null {
        const selection = this.authService.getViewAsState();
        if (!selection) {
            return null;
        }
        return { selection, labels: this.readLabels(selection) };
    }

    start(selection: ViewAsSelection, labels: ViewAsLabels): Observable<LoginResponse> {
        return this.authService.startViewAs(selection).pipe(
            tap(() => {
                const stored: StoredViewAsLabels = { ...selection, ...labels };
                localStorage.setItem(LocalStorageConstants.viewAsLabels, JSON.stringify(stored));
                this.reloadApp(RoutePaths.dashboard);
            }),
        );
    }

    stop(): Observable<LoginResponse> {
        return this.authService.stopViewAs().pipe(
            tap(() => {
                localStorage.removeItem(LocalStorageConstants.viewAsLabels);
                this.reloadApp(this.router.url);
            }),
        );
    }

    // A full reload is what guarantees nothing from the previous context survives: permissions, menus,
    // the header org tree, service caches and the CacheInterceptor are all held in memory, and AuthGuard
    // reloads the role permissions for the new token on the way back in. The cached org selection may
    // be outside the new org-level scope, so it is dropped too.
    reloadApp(url: string): void {
        localStorage.removeItem(LocalStorageConstants.selectedOrg);
        window.location.assign(url);
    }

    private onPreviewDropped(): void {
        localStorage.removeItem(LocalStorageConstants.viewAsLabels);
        localStorage.setItem(LocalStorageConstants.viewAsEndedNotice, 'true');
        this.reloadApp(this.router.url);
    }

    // The labels are a display cache written when the preview started; the token is the source of truth,
    // so they are only used while they describe the preview the token carries.
    private readLabels(selection: ViewAsSelection): ViewAsLabels | null {
        const raw = localStorage.getItem(LocalStorageConstants.viewAsLabels);
        if (!raw) {
            return null;
        }
        try {
            const stored = JSON.parse(raw) as StoredViewAsLabels | null;
            if (!stored || toStateKey(stored) !== toStateKey(selection)) {
                return null;
            }
            return { roleName: stored.roleName, organizationLevelNames: stored.organizationLevelNames ?? [] };
        } catch {
            return null;
        }
    }

    private showEndedNotice(): void {
        if (!localStorage.getItem(LocalStorageConstants.viewAsEndedNotice)) {
            return;
        }
        localStorage.removeItem(LocalStorageConstants.viewAsEndedNotice);
        this.translateService.get(VIEW_AS_ENDED_MESSAGE).pipe(take(1)).subscribe({
            next: (message: string) => this.toastService.showInfo(message),
        });
    }

    // Tokens live in localStorage, so a preview started, switched or ended in another tab silently
    // changes this tab's access; reload so its in-memory permissions and data match the new token.
    private readonly onStorageChange = (event: StorageEvent): void => {
        if (event.key === LocalStorageConstants.accessToken && toStateKey(this.authService.getViewAsState()) !== this.loadedStateKey) {
            this.ngZone.run(() => window.location.reload());
        }
    };
}

function toStateKey(selection: ViewAsSelection | null): string {
    if (!selection) {
        return '';
    }
    const orgLevelIds = [...new Set(selection.organizationLevelIds ?? [])].sort();
    return `${selection.roleId}|${orgLevelIds.join(',')}`;
}
